import React, { useEffect, useState } from "react";
import { useModalStore } from "@/hooks/useModalStore";
import { Form, Input, Modal } from "antd";
import { useTranslation } from "react-i18next";
import { websiteValidator } from "@/biz/validator";
import { submitInfluencerLinkApi } from "@/api/order";
import { toast } from "aha-components";
import PreviousInfluencerLinkShortcut from "./PreviousInfluencerLinkShortcut";

export default function ModalInfluencerTrackLink() {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const {
    showInfluencerTrackLinkModalInfo,
    setShowInfluencerTrackLinkModalInfo,
  } = useModalStore();
  const { visible, orderId, campaignId, isContent, updateFunc, link, sourcePromotionalLink } =
    showInfluencerTrackLinkModalInfo;
  const [loading, setLoading] = useState(false);
  // 入口传入当前订单的历史推广链接；空值时快捷填充区域整行隐藏。
  const previousInfluencerLink = sourcePromotionalLink?.trim() || undefined;

  useEffect(() => {
    if (!visible) return;

    // Modal 默认不会销毁内部表单。每次切换订单或重新打开时主动同步当前链接，
    // 避免把上一位达人的链接残留到新的订单中。
    form.setFieldsValue({ utmLink: link ?? "" });
  }, [form, link, orderId, visible]);

  const handleCancel = () => {
    // 关闭时清空表单状态；下次打开后由上面的 effect 写入当前订单链接。
    form.resetFields();
    if (!isContent) {
      updateFunc?.();
    }
    setShowInfluencerTrackLinkModalInfo({
      visible: false,
      orderId: "",
      campaignId: "",
      isContent: false,
    });
  };

  return (
    <Modal
      zIndex={1200}
      open={visible}
      width={832}
      title={t("common.influencerTrackLinkModal.title", {
        defaultValue: "Assign a unique promotional link to this influencer",
      })}
      okText={
        isContent
          ? t("common.buttons.approve", { defaultValue: "Approve" })
          : t("common.buttons.confirm", { defaultValue: "Confirm" })
      }
      cancelText={
        isContent
          ? t("common.buttons.cancel", { defaultValue: "Cancel" })
          : t("common.buttons.notNow", { defaultValue: "Not now" })
      }
      okButtonProps={{
        size: "large",
      }}
      cancelButtonProps={{
        size: "large",
      }}
      onOk={() => {
        form
          .validateFields()
          .then(async (values: any) => {
            try {
              setLoading(true);
              const res = await submitInfluencerLinkApi(orderId, {
                utmLink: values.utmLink,
              });
              if (res.code === 0) {
                toast({
                  message: t('common.influencerTrackLinkModal.confirmSuccess', { 
                    defaultValue: "Submission successful" 
                  }),
                  type: "success",
                  duration: 3000,
                  position: "top-right",
                });
              }
              await updateFunc?.();
              handleCancel();
            } catch (error) {
              console.error(error);
            } finally {
              setLoading(false);
            }
          })
          .catch((error) => {
            console.error(error);
          });
      }}
      onCancel={handleCancel}
      confirmLoading={loading}
    >
      <div className="flex flex-col gap-8">
        <div className="justify-start font-['Inter'] text-sm font-normal leading-5 text-[#525252]">
          {t("common.influencerTrackLinkModal.description", {
            defaultValue:
              "The influencer will include this link in their post or link in bio so their audience is directed to your destination.",
          })}{" "}
        </div>
        <div className="flex flex-col gap-2">
          <div className="inline-flex justify-start items-start gap-0.5">
            <div className="justify-start text-Colors-Text-text-secondary-(700) text-sm font-medium font-['DM_Sans'] leading-5">
              {t("common.influencerTrackLinkModal.uniqueLinkLabel", {
                defaultValue: "Unique link",
              })}
            </div>
            <div className="justify-start text-[#EC470A] text-sm font-medium font-['DM_Sans'] leading-5">
              *
            </div>
          </div>
          <Form name="utmLinkForm" form={form}>
            <Form.Item
              name="utmLink"
              rules={[
                {
                  required: true,
                  message: t(
                    "common.influencerTrackLinkModal.uniqueLinkRequired",
                    {
                      defaultValue:
                        "Please enter a unique promotional link for this influencer",
                    },
                  ),
                },
                {
                  validator: (_, value) => {
                    if (value && !websiteValidator(value?.trim(), false, false)) {
                      return Promise.reject(
                        new Error(
                          t(
                            "campaignCreate.budgetBidding.pleaseEnterValidLink",
                            { defaultValue: "Please enter a valid link" },
                          ),
                        ),
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
              className="mb-0"
            >
              <Input
                placeholder={t(
                  "common.influencerTrackLinkModal.uniqueLinkPlaceholder",
                  { defaultValue: "Paste the influencer-specific URL" },
                )}
                size="large"
              />
            </Form.Item>
          </Form>
          <PreviousInfluencerLinkShortcut
            previousInfluencerLink={previousInfluencerLink}
            onUse={(previousLink) => {
              form.setFieldsValue({ utmLink: previousLink });
            }}
          />
        </div>
        <div className="w-full border-t border-[#E5E5E5]"></div>
      </div>
    </Modal>
  );
}
